package com.castly.shared.dto

import kotlinx.serialization.Serializable

/**
 * Admin Section DTO for admin CRUD operations
 * Includes soft delete status and full metadata
 */
@Serializable
data class AdminSectionDto(
    val id: String,
    val homePageId: String,
    val sectionType: String, // "static", "banner", "carousel" or "episode_carousel"
    val internalName: String,
    val displayOrder: Int,

    // Static section
    val vendorId: String? = null,

    // Banner section
    val bannerImageEn: String? = null,
    val bannerImageAr: String? = null,
    val bannerLink: String? = null,

    // Carousel section
    val contentType: String? = null, // "podcast" or "audiobook"
    val itemCount: Int? = null, // Number of items in carousel (null for non-carousel sections)

    // Soft delete status
    val deletedAt: String? = null,

    // Translations for carousel sections
    val translations: List<TranslationDto>? = null,

    // Audit timestamps
    val createdAt: String,
    val updatedAt: String
) {
    /**
     * Translation DTO for section translations
     */
    @Serializable
    data class TranslationDto(
        val languageCode: String,
        val title: String,
        val description: String? = null
    )
}

/**
 * Request DTO for creating a new section
 */
@Serializable
data class CreateSectionRequest(
    val sectionType: String,
    val internalName: String,
    val displayOrder: Int? = null,

    // Optional fields based on section type
    val vendorId: String? = null,
    val bannerImageEn: String? = null,
    val bannerImageAr: String? = null,
    val bannerLink: String? = null,
    val contentType: String? = null,

    // Carousel translations
    val translations: List<SectionTranslationData>? = null
)

/**
 * Request DTO for updating an existing section
 */
@Serializable
data class UpdateSectionRequest(
    val internalName: String? = null,
    val displayOrder: Int? = null,
    val vendorId: String? = null,
    val bannerImageEn: String? = null,
    val bannerImageAr: String? = null,
    val bannerLink: String? = null,
    val contentType: String? = null,
    val translations: List<SectionTranslationData>? = null
)

/**
 * Translation data for carousel sections
 */
@Serializable
data class SectionTranslationData(
    val languageCode: String,
    val title: String,
    val description: String? = null
)

/**
 * Request DTO for batch reordering sections
 */
@Serializable
data class ReorderSectionsRequest(
    val sectionIds: List<String>
)

/**
 * Request DTO for batch reordering items within a carousel section
 */
@Serializable
data class ReorderSectionItemsRequest(
    val itemIds: List<String>
)
